import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const cls = 'w-full px-3 py-2.5 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none font-body text-sm';

const HARGA_PRESETS = [10000, 11000, 12000, 13000, 14000, 15000, 16000, 17000];

export default function ZakatPengaturanPane({ profile, onSaved }) {
  const [form, setForm] = useState({
    entity_name: profile?.entity_name || '',
    nama_amil: profile?.nama_amil || '',
    phone: profile?.phone || '',
    alamat: profile?.alamat || '',
    nilai_beras_per_jiwa: profile?.nilai_beras_per_jiwa || 2.5,
    harga_beras_per_kg: profile?.harga_beras_per_kg || 15000,
    nominal_uang_per_jiwa: profile?.nominal_uang_per_jiwa || 37500,
    harga_beras_options: profile?.harga_beras_options?.length > 0
      ? profile.harga_beras_options
      : [...HARGA_PRESETS],
    status: profile?.status || 'aktif',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };
    if (name === 'nilai_beras_per_jiwa' || name === 'harga_beras_per_kg') {
      updated.nominal_uang_per_jiwa = parseFloat(updated.nilai_beras_per_jiwa || 0) * parseFloat(updated.harga_beras_per_kg || 0);
    }
    setForm(updated);
  };

  const toggleHarga = (h) => {
    setForm(f => {
      const opts = f.harga_beras_options || [];
      const next = opts.includes(h) ? opts.filter(x => x !== h) : [...opts, h].sort((a, b) => a - b);
      return { ...f, harga_beras_options: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/zakat-ramadan/profile`, form, cfg);
      toast.success('Pengaturan disimpan');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const selectedOptions = form.harga_beras_options || [];

  return (
    <form onSubmit={handleSubmit} className="max-w-xl space-y-4" data-testid="zakat-pengaturan">
      <h3 className="text-sm font-semibold text-stone-700 mb-3">Pengaturan Amil & Nilai Zakat</h3>
      <div>
        <label className="block text-xs text-stone-500 mb-1.5">Nama Masjid/Lembaga</label>
        <input name="entity_name" value={form.entity_name} onChange={handleChange} className={cls} placeholder="Nama lembaga" />
      </div>
      <div>
        <label className="block text-xs text-stone-500 mb-1.5">Nama Amil</label>
        <input name="nama_amil" value={form.nama_amil} onChange={handleChange} className={cls} placeholder="Nama amil/penanggung jawab" />
      </div>
      <div>
        <label className="block text-xs text-stone-500 mb-1.5">No. HP Amil</label>
        <input name="phone" value={form.phone} onChange={handleChange} className={cls} placeholder="08xxxxxxxxxx" />
      </div>
      <div>
        <label className="block text-xs text-stone-500 mb-1.5">Alamat</label>
        <textarea name="alamat" value={form.alamat} onChange={handleChange} className={cls} rows={2} placeholder="Alamat masjid/lembaga" />
      </div>

      {/* Nilai Zakat Fitrah */}
      <div className="bg-emerald-50 rounded-xl p-4 space-y-3">
        <p className="text-xs font-semibold text-emerald-800">Nilai Zakat Fitrah</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-stone-500 mb-1">Beras/Jiwa (kg)</label>
            <input name="nilai_beras_per_jiwa" type="number" step="0.1" min="0.5" value={form.nilai_beras_per_jiwa} onChange={handleChange} className={cls} />
          </div>
          <div>
            <label className="block text-xs text-stone-500 mb-1">Harga Beras Default (Rp/kg)</label>
            <input name="harga_beras_per_kg" type="number" min="1000" value={form.harga_beras_per_kg} onChange={handleChange} className={cls} />
          </div>
        </div>
        <div className="pt-1">
          <label className="block text-xs text-stone-500 mb-1">Uang/Jiwa (Rp) — Otomatis</label>
          <input name="nominal_uang_per_jiwa" type="number" min="0" value={form.nominal_uang_per_jiwa} onChange={handleChange} className={cls} readOnly />
        </div>
      </div>

      {/* Pilihan Harga Beras */}
      <div className="bg-amber-50 rounded-xl p-4 space-y-3 border border-amber-100">
        <div>
          <p className="text-xs font-semibold text-amber-800">Pilihan Harga Beras untuk Muzakki</p>
          <p className="text-xs text-amber-600 mt-0.5">Centang harga yang tersedia saat input transaksi</p>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {HARGA_PRESETS.map(h => (
            <label key={h} className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl border text-xs font-medium cursor-pointer transition-colors ${
              selectedOptions.includes(h)
                ? 'bg-amber-600 border-amber-600 text-white'
                : 'border-amber-200 text-amber-700 hover:bg-amber-100'
            }`}>
              <input type="checkbox" checked={selectedOptions.includes(h)} onChange={() => toggleHarga(h)} className="sr-only" />
              Rp {(h/1000).toFixed(0)}rb
            </label>
          ))}
        </div>
        {selectedOptions.length === 0 && (
          <p className="text-xs text-amber-600">Pilih minimal satu harga. Jika kosong, semua harga akan ditampilkan.</p>
        )}
        {selectedOptions.length > 0 && (
          <p className="text-xs text-amber-700">
            {selectedOptions.length} pilihan aktif: {selectedOptions.map(h => `Rp ${(h/1000).toFixed(0)}rb`).join(', ')}
          </p>
        )}
      </div>

      <div>
        <label className="block text-xs text-stone-500 mb-1.5">Status</label>
        <select name="status" value={form.status} onChange={handleChange} className={cls}>
          <option value="aktif">Aktif</option>
          <option value="tidak_aktif">Tidak Aktif</option>
        </select>
      </div>
      <button type="submit" disabled={saving}
        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-60">
        <Save className="w-4 h-4" /> {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
      </button>
    </form>
  );
}
